import { useSyncExternalStore } from 'react';

/**
 * Options whose price-break nudge the server refused because the vendor offer
 * it was priced on changed (PRICE_BREAK_OFFER_CHANGED).
 *
 * The nudge is frozen onto the option's buy leg by the engine run, so a refetch
 * brings the same stale offer back; the server will refuse it every time. The
 * board therefore stops offering it for the rest of the page's life. The next
 * engine run writes new option ids, so fresh provenance shows its chip again
 * without anything here being cleared.
 *
 * Module state on purpose: not persisted, not per component, shared by every
 * chip that renders the same option.
 */
const dismissed = new Set<string>();
const listeners = new Set<() => void>();

export function dismissPriceBreakNudge(optionId: string): void {
  if (dismissed.has(optionId)) return;
  dismissed.add(optionId);
  for (const listener of listeners) listener();
}

function subscribe(onStoreChange: () => void): () => void {
  listeners.add(onStoreChange);
  return () => {
    listeners.delete(onStoreChange);
  };
}

/** Whether the board has stopped offering this option's price-break nudge. */
export function useIsPriceBreakNudgeDismissed(
  optionId: string | null | undefined,
): boolean {
  const isDismissed = () => optionId != null && dismissed.has(optionId);
  return useSyncExternalStore(subscribe, isDismissed, isDismissed);
}
